import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const ArtichokePage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Artichoke Success: Perfect pH for Premium Buds"
        description="Research-backed pH management for profitable artichoke production. Learn how water quality affects your artichoke crop yield and quality."
        canonicalPath="/artichoke"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1263986/pexels-photo-1263986.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Artichoke plants with developing buds in agricultural field"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/80 via-emerald-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🌿</span>
              <span>Premium Vegetable Resource</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Artichoke Success:
              <span className="block text-emerald-300">Perfect pH for Premium Buds</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management for profitable artichoke production
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-emerald-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-emerald-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🌿</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-emerald-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Soil pH isn't just a number—it governs nutrient availability, root health, and ultimately bud yield and quality.</strong> Your irrigation water could be quietly pushing your soil pH out of the optimal range season after season.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped growers optimize their water quality for 40+ years. This guide explains why your irrigation water pH matters for artichokes, what research shows, and how sulfur burners can protect your soil, enhance your bud quality, and maximize your profits.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Why Soil pH Matters for Artichokes */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🌱</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Why Soil pH Matters for Artichokes
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 md:p-8 border border-emerald-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-emerald-500 pl-6">
                "Artichokes thrive in slightly acidic to neutral soils, around pH 6.0–7.0, with many extension guides recommending 6.5–7.0. They can handle slightly alkaline soils, even up to pH 8.0, but performance is best within the ideal range."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-emerald-600" size={16} />
                <span className="text-sm font-medium text-emerald-600">
                  Source: Multiple Agricultural Extension Sources
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Maintaining the right soil pH ensures nutrient availability, robust roots, and vigorous growth for maximum bud production.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Impact of pH on Artichoke Health */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">⚠️</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: Impact of pH on Artichoke Health
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                At suboptimal pH levels, essential nutrients like nitrogen, phosphorus, potassium, iron, and manganese become less available—even if present in the soil. Artichokes are sensitive to waterlogged soils and nutrient imbalances, both of which are exacerbated by poor pH management.
              </p>
              
              <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Key pH Effects:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">Nutrient Absorption</h4>
                    <p className="text-sm text-gray-700">Essential nutrients become chemically locked away outside the 6-7 pH zone</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">Soil Structure</h4>
                    <p className="text-sm text-gray-700">Ideal pH supports good soil aggregation and water infiltration</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">Root Vigor</h4>
                    <p className="text-sm text-gray-700">Proper pH prevents root diseases and nutrient imbalances</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Proper pH management is crucial for managing moisture and preventing root diseases that artichokes are particularly sensitive to.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Agronomic Best Practices */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🛠️</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Agronomic Best Practices
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">✅ Soil Testing</h3>
                  <p className="text-gray-700 mb-4">
                    Conduct comprehensive soil tests (pH, nutrient levels, CEC) every 2–3 years, and before establishing new beds. Adjust pH based on test results—don't guess.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">✅ Adjusting Soil pH</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2">Too Acidic (&lt;6.0)</h4>
                      <p className="text-sm text-gray-700">Apply lime per test recommendations to raise it</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <h4 className="font-semibold text-orange-800 mb-2">Too Alkaline (&gt;7.5)</h4>
                      <p className="text-sm text-gray-700">Though artichokes tolerate some alkalinity, excessive soil pH may warrant sulfur or acidifying fertilizers</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">✅ Soil Preparation & Amendments</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Use deep, fertile, well-drained soil—sandy loam is ideal</li>
                    <li>• Incorporate plenty of organic matter—compost or manure—to improve structure</li>
                    <li>• Raised beds or mounds help with drainage and avoid root rot</li>
                    <li>• Provide full sun (4–6+ hours daily), with partial shade in hotter climates</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: pH-Driven Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🎯</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: pH-Driven Benefits
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                Balanced pH supports healthy root systems, which drives larger, more consistent buds. Proper nutrition leads to quality traits: enhanced flavor, texture, and uniform bud development.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Bud Quality Improvements</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Bigger, more uniform flower buds</li>
                    <li>• Enhanced flavor and texture</li>
                    <li>• Better bud development timing</li>
                    <li>• Improved harvest consistency</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Plant Performance</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Robust root systems for better nutrient uptake</li>
                    <li>• Reduced stress and disease susceptibility</li>
                    <li>• Multi-year production in suitable climates</li>
                    <li>• Better drought resistance</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">Research Note:</span>
                </div>
                <p className="text-gray-700">Maintaining soil pH between 6.0 and 7.0 prevents root toxicity and nutrient lock-out while enhancing water balance and supporting stronger yield and bud quality.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Expert Insights & Quick Checklist */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🧠</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Expert Insights & Quick Checklist
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 md:p-8 border border-teal-200">
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Expert Quotes</h3>
                  <div className="space-y-4">
                    <blockquote className="text-gray-700 italic border-l-4 border-teal-500 pl-4">
                      "Aim for an optimal soil pH of between 6.5 and 7.0." — Kellogg Garden Organics
                    </blockquote>
                    <blockquote className="text-gray-700 italic border-l-4 border-teal-500 pl-4">
                      "Soil should be kept at a pH level of 6.0 to 6.5. Lime or gypsum can be added if calcium is low." — UC IPM
                    </blockquote>
                    <blockquote className="text-gray-700 italic border-l-4 border-teal-500 pl-4">
                      "Artichokes grow best in a soil pH of 6.0 to 7.0. They require well‑drained, organic-rich soil." — U.F. Seeds
                    </blockquote>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick pH Checklist for Artichoke Growers</h3>
                  <div className="space-y-3">
                    {[
                      { step: '1', action: 'Test soil pH, nutrients, and CEC' },
                      { step: '2', action: 'Adjust pH to 6.0–7.0 (lime if &lt;6.0; acidify only if &gt;7.5)' },
                      { step: '3', action: 'Amend soil with compost and ensure drainage' },
                      { step: '4', action: 'Plant in full sun, spaced 4–6 ft apart' },
                      { step: '5', action: 'Maintain moisture with deep watering weekly' },
                      { step: '6', action: 'Fertilize with nitrogen monthly during active growth' },
                      { step: '7', action: 'Harvest & prune buds, remove spent growth' },
                      { step: '8', action: 'Pest & disease control, and overwinter where applicable' }
                    ].map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-teal-600 font-bold text-sm">{item.step}</span>
                        </div>
                        <span className="text-gray-700">{item.action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6 mt-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Artichokes:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents nutrient lockout and toxicity',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    'Automated operation with minimal maintenance',
                    '40+ years of proven results in vegetable crops'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Artichokes" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ArtichokePage;